import { Directive, Host, OnDestroy, OnInit } from '@angular/core';
import { Editor } from 'primeng/editor';
import { LinkFormattingService } from '../services/link-formatting.service';

/**
 * Automatically verifies and formats HTTPS links in PrimeNG p-editor fields.
 */
@Directive({
  selector: 'p-editor'
})
export class PrimeEditorLinkFormatDirective implements OnInit, OnDestroy {
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private textChangeSub: { unsubscribe: () => void } | null = null;
  private isApplyingFormat = false;

  constructor(
    @Host() private editor: Editor,
    private linkFormattingService: LinkFormattingService
  ) {}

  ngOnInit(): void {
    this.textChangeSub = this.editor.onTextChange.subscribe(() => this.scheduleFormatting());
  }

  ngOnDestroy(): void {
    this.textChangeSub?.unsubscribe();
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  private scheduleFormatting(): void {
    if (this.isApplyingFormat) {
      return;
    }

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => void this.applyFormatting(), 500);
  }

  private async applyFormatting(): Promise<void> {
    const quill = this.editor.quill;
    if (!quill) {
      return;
    }

    const html = quill.root.innerHTML;
    if (!html || html === '<p><br></p>') {
      return;
    }

    const result = await this.linkFormattingService.processNewUrls(html, true);
    if (!result.modified || result.texte === html) {
      return;
    }

    this.isApplyingFormat = true;
    try {
      const selection = quill.getSelection();
      quill.clipboard.dangerouslyPasteHTML(result.texte);
      if (selection) {
        quill.setSelection(selection.index, selection.length);
      }
    } finally {
      this.isApplyingFormat = false;
    }
  }
}
